import { useEffect } from "react";
import { FAQS } from "../data/venpa";
import { LOGO_URL, FOUNDER_PHOTO_URL } from "../data/brand";
import { SITE_URL } from "./Seo";

const ORIGIN = SITE_URL;

// Stable @id values so the same real-world entities (the company, its
// founder) are recognizable as the same node wherever they're referenced,
// even though each page's JSON-LD is self-contained (JSON-LD from one page
// isn't merged with another page's by search engines, so entities are
// repeated in full on every page that needs them — only the @id is shared).
export const ORGANIZATION_ID = `${ORIGIN}/#organization`;
export const PERSON_ID = `${ORIGIN}/#person-vanishree-venugopal`;

// Minimal, reusable fragments — the single source of truth for the fields
// that get repeated across pages (BlogPosting.publisher/author), so the
// full entity definitions below and every reference elsewhere can never
// drift out of sync with each other.
const ORGANIZATION_REF = { "@type": "Organization", "@id": ORGANIZATION_ID, name: "Venpa AI", url: ORIGIN, logo: LOGO_URL };
const PERSON_REF = { "@type": "Person", "@id": PERSON_ID, name: "Vanishree Venugopal", jobTitle: "Founder & CEO", image: FOUNDER_PHOTO_URL };

/**
 * Reusable JSON-LD injector (upsert-by-id in <head>, cleaned up on unmount).
 * Shared so every page that needs structured data (Landing, and the
 * breadcrumb schema on /blog and /blog/:slug) goes through one
 * implementation instead of each hand-rolling its own DOM manipulation.
 */
export function JsonLdScript({ id, data }) {
  const serialized = JSON.stringify(data);

  useEffect(() => {
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = serialized;

    return () => {
      const existing = document.getElementById(id);
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, serialized]);

  return null;
}

// sameAs intentionally omitted: the LinkedIn URL previously here
// (linkedin.com/in/vani-shreechampion/) is a *personal* profile — it
// represents the founder, not the Organization, so it belongs on the
// Person entity below instead (schema audit Phase 6, Part 3). No verified
// company-level social profile exists yet, so this is left empty rather
// than fabricated.
export const ORGANIZATION = {
  "@context": "https://schema.org",
  ...ORGANIZATION_REF,
  alternateName: "The Autonomous AI Agency",
  description:
    "Venpa AI is an autonomous AI agency engineering AI chatbots, AI voice agents and full-stack AI automation for high-growth brands in India and worldwide.",
  foundingLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressLocality: "Coimbatore", addressCountry: "IN" } },
  contactPoint: [{ "@type": "ContactPoint", email: "info@venpa.co.in", contactType: "Sales" }],
  areaServed: ["India", "United States", "United Arab Emirates", "United Kingdom", "Europe"],
  knowsAbout: [
    "AI Automation",
    "AI Chatbot",
    "AI Voice Agent",
    "Generative Engine Optimization",
    "Answer Engine Optimization",
    "SEO",
    "Autonomous Lead Generation",
    "RAG pipelines",
    "AI Twin",
  ],
};

// Person schema for the founder — every field here already appears on the
// page today (name/title/photo in the Testimonials section; she's the
// named author of every blog post). The real personal LinkedIn URL that
// used to be misattributed to Organization.sameAs belongs here instead.
export const PERSON = {
  "@context": "https://schema.org",
  ...PERSON_REF,
  worksFor: ORGANIZATION_REF,
  sameAs: ["https://www.linkedin.com/in/vani-shreechampion/"],
};

// No site search feature exists — a SearchAction was previously declared
// here pointing at a nonexistent `/?q=` endpoint (audit TS-11/SS-08).
// Removed rather than left describing a feature that doesn't work.
const WEBSITE = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Venpa AI",
  url: ORIGIN,
  publisher: ORGANIZATION_REF,
};

/** Shared BreadcrumbList builder used by /blog and /blog/:slug. */
export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${ORIGIN}${item.path}`,
    })),
  };
}

/** Shared FAQPage builder — used by the homepage FAQ and per-post FAQs. */
export function faqPageSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/** BlogPosting schema — built entirely from fields already in BLOG_POSTS. */
export function blogPostingSchema(post) {
  // ImageObject with real, measured dimensions when known (data/venpa.js
  // coverWidth/coverHeight); falls back to a plain URL string (still valid
  // schema.org) rather than guessing dimensions that aren't verified.
  const image = post.coverWidth && post.coverHeight
    ? { "@type": "ImageObject", url: post.cover, width: post.coverWidth, height: post.coverHeight }
    : post.cover;

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image,
    datePublished: post.date,
    author: PERSON_REF,
    publisher: ORGANIZATION_REF,
    mainEntityOfPage: { "@type": "WebPage", "@id": `${ORIGIN}/blog/${post.slug}` },
  };
}

/** Generic ItemList builder — reused for the homepage services list, the
 * blog index, and the 12-package loop referenced in blog post 3. */
export function itemListSchema(name, items) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      ...item,
    })),
  };
}

export default function JsonLd() {
  return (
    <>
      <JsonLdScript id="ld-organization" data={ORGANIZATION} />
      <JsonLdScript id="ld-person" data={PERSON} />
      <JsonLdScript id="ld-faqpage" data={faqPageSchema(FAQS)} />
      <JsonLdScript id="ld-website" data={WEBSITE} />
    </>
  );
}
